import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  setStep, nextStep, previousStep, updateAnswers,
  setSmartDefaults, setSmartDefaultsLoading, applySmartDefaults,
} from '../store/questionnaireSlice';
import { setSelectedTaxYear } from '../store/analysisSlice';
import Layout from '../components/Layout';
import { questionnaireApi } from '../services/api';
import { ChevronLeft, ChevronRight, Save, CheckCircle2 } from 'lucide-react';
import DetectedInfoStep from '../components/questionnaire/DetectedInfoStep';
import IncomeReviewStep from '../components/questionnaire/IncomeReviewStep';
import DeductionsAdjustmentsStep from '../components/questionnaire/DeductionsAdjustmentsStep';
import AdditionalInfoStep from '../components/questionnaire/AdditionalInfoStep';
import SmartReviewStep from '../components/questionnaire/SmartReviewStep';

export default function Questionnaire() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentStep, totalSteps, answers, smartDefaults, smartDefaultsLoading } = useSelector(
    (state: RootState) => state.questionnaire
  );
  const selectedTaxYear = useSelector((state: RootState) => state.analysis.selectedTaxYear);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load smart defaults first (to detect tax year), then load saved answers
  useEffect(() => {
    dispatch(setSmartDefaultsLoading(true));

    // First: fetch smart defaults to detect the actual tax year from documents
    questionnaireApi.getSmartDefaults(selectedTaxYear || 2025).then((defaultsRes) => {
      const detectedYear = defaultsRes?.data?.detectedTaxYear || selectedTaxYear || 2025;

      // Update Redux tax year if documents indicate a different year
      if (detectedYear !== selectedTaxYear) {
        dispatch(setSelectedTaxYear(detectedYear));
      }

      // Apply smart defaults
      if (defaultsRes?.data) {
        dispatch(setSmartDefaults(defaultsRes.data));
        dispatch(applySmartDefaults());
      }

      // Now load saved answers for the correct tax year
      return questionnaireApi.getAnswers(detectedYear).catch(() => null);
    }).then((savedRes) => {
      if (savedRes?.data?.questionnaire_data) {
        const saved = typeof savedRes.data.questionnaire_data === 'string'
          ? JSON.parse(savedRes.data.questionnaire_data)
          : savedRes.data.questionnaire_data;
        const { _currentStep, ...savedAnswers } = saved;
        if (!savedAnswers.filingStatus && savedRes.data.filing_status) {
          savedAnswers.filingStatus = savedRes.data.filing_status;
        }
        dispatch(updateAnswers(savedAnswers));
        if (_currentStep && _currentStep >= 1 && _currentStep <= 5) {
          dispatch(setStep(_currentStep));
        }
      }
    }).catch(() => {
      // No defaults or saved data — start fresh
    }).finally(() => setLoading(false));
    // Bootstrap-only: reads selectedTaxYear as an initial hint, then
    // dispatches setSelectedTaxYear to the detected year. Including it in the
    // deps would cause the bootstrap to re-run on every year change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Auto-save questionnaire progress
  useEffect(() => {
    if (loading) return;
    if (!answers.filingStatus) return;
    const taxYear = selectedTaxYear || 2025;
    const saveData = { ...answers, _currentStep: currentStep };
    questionnaireApi.saveAnswers(taxYear, answers.filingStatus || 'single', saveData).catch(() => {});
  }, [answers, currentStep, loading, selectedTaxYear]);

  const handleNext = () => {
    if (currentStep < totalSteps) dispatch(nextStep());
  };

  const handlePrevious = () => {
    if (currentStep > 1) dispatch(previousStep());
  };

  const handleSaveProgress = async () => {
    setSaving(true);
    try {
      const taxYear = selectedTaxYear || 2025;
      const saveData = { ...answers, _currentStep: currentStep };
      await questionnaireApi.saveAnswers(taxYear, answers.filingStatus || 'single', saveData);
    } catch {
      // Silent fail
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <DetectedInfoStep />;
      case 2:
        return <IncomeReviewStep />;
      case 3:
        return <DeductionsAdjustmentsStep />;
      case 4:
        return <AdditionalInfoStep />;
      case 5:
        return <SmartReviewStep />;
      default:
        return null;
    }
  };

  if (loading || smartDefaultsLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-500">Loading your tax information...</div>
        </div>
      </Layout>
    );
  }

  const stepLabels = [
    'Your Info',
    'Income',
    'Deductions',
    'Additional',
    'Review',
  ];

  return (
    <Layout>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax Questionnaire</h1>
        <p className="text-gray-500">
          {smartDefaults && smartDefaults.documentsStatus.analyzed > 0
            ? `We've pre-filled answers from your ${smartDefaults.documentsStatus.analyzed} analyzed document${smartDefaults.documentsStatus.analyzed !== 1 ? 's' : ''}. Review and confirm.`
            : 'Answer a few questions about your income and deductions.'}
        </p>
      </div>

      {/* Step Indicators */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {stepLabels.map((label, i) => {
            const stepNum = i + 1;
            const isActive = stepNum === currentStep;
            const isComplete = stepNum < currentStep;
            return (
              <button
                key={label}
                onClick={() => dispatch(setStep(stepNum))}
                className={`flex items-center space-x-2 text-sm font-medium transition-colors
                  ${isActive ? 'text-gray-900' : isComplete ? 'text-gray-500' : 'text-gray-300'}`}
              >
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                  ${isActive ? 'bg-gray-900 text-white' : isComplete ? 'bg-gray-200 text-gray-600' : 'bg-gray-100 text-gray-400'}`}>
                  {isComplete ? '✓' : stepNum}
                </span>
                <span className="hidden sm:inline">{label}</span>
              </button>
            );
          })}
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          <div
            className="bg-gray-900 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-8">
        {renderStep()}
      </div>

      {/* Navigation */}
      <div className="flex gap-3 justify-between">
        <div className="flex gap-3">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="px-5 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center space-x-1.5"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Previous</span>
          </button>

          <button
            onClick={handleNext}
            disabled={currentStep === totalSteps}
            className="px-5 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center space-x-1.5"
          >
            <span>Next</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleSaveProgress}
            disabled={saving}
            className="px-5 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 transition-colors flex items-center space-x-1.5"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save'}</span>
          </button>

          {currentStep === totalSteps && (
            <button
              onClick={() => navigate('/review')}
              className="px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Complete & Review</span>
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
